import { useCallback, useEffect, useMemo, useState } from 'react';

import { createEmptySurchargeSession, withPeriodsOnly } from './surchargeGridsModel';
import type { SurchargePeriod, SurchargePeriodFormValues, SurchargeSessionState } from './types';

export function useSurchargeGrids(offerId: string | undefined) {
  const [session, setSession] = useState<SurchargeSessionState>(createEmptySurchargeSession);

  useEffect(() => {
    setSession(createEmptySurchargeSession());
  }, [offerId]);

  const addPeriod = useCallback((values: SurchargePeriodFormValues) => {
    const period: SurchargePeriod = { ...values, id: crypto.randomUUID() };
    setSession((previous) => ({
      ...previous,
      grids: { ...previous.grids, periods: [...previous.grids.periods, period] },
    }));
  }, []);

  const updatePeriod = useCallback((id: string, values: SurchargePeriodFormValues) => {
    setSession((previous) => ({
      ...previous,
      grids: {
        ...previous.grids,
        periods: previous.grids.periods.map((period) => (period.id === id ? { ...values, id } : period)),
      },
    }));
  }, []);

  const replacePeriods = useCallback((periods: Array<SurchargePeriodFormValues>) => {
    setSession((previous) => ({
      ...previous,
      grids: { ...previous.grids, periods: periods.map((values) => ({ ...values, id: crypto.randomUUID() })) },
    }));
  }, []);

  const removePeriod = useCallback((id: string) => {
    setSession((previous) => ({
      ...previous,
      grids: { ...previous.grids, periods: previous.grids.periods.filter((period) => period.id !== id) },
    }));
  }, []);

  const loadDraft = useCallback((draftSession: SurchargeSessionState) => {
    setSession(withPeriodsOnly(draftSession));
  }, []);

  return useMemo(
    () => ({
      session,
      addPeriod,
      updatePeriod,
      replacePeriods,
      removePeriod,
      loadDraft,
    }),
    [session, addPeriod, updatePeriod, replacePeriods, removePeriod, loadDraft],
  );
}
